import { memo } from 'react';
import styled from 'styled-components';
import { insertTrackEvent } from '@comeon/analytics';
import { IconWithLabel } from '../icons/icons';
import * as Paths from '../router/constants';
import { useTypedTranslations } from '../translation/use-translations';

const IconWrapper = styled.div.attrs<{ 'data-at'?: string }>({
	'data-at': 'product-nav-wrapper'
})<{ numProducts: number }>`
	display: flex;
	justify-content: center;

	// Less then breakpoint.lg
	@media (max-width: 768px) {
		width: ${props => (props.numProducts === 4 ? '200px' : '300px')};
		flex-wrap: wrap;
		span {
			flex-basis: 100px;
			align-content: center;
			text-align: center;
			justify-content: center;
			label {
				margin: 8px 0 20px 0 !important;
			}
		}
	}
`;

const ProductIconWithLabel = styled(IconWithLabel)<{ badge?: React.ReactNode; small?: boolean }>`
	margin: 0 2px;
	text-align: center;

	label {
		color: var(--product-icon-label, rgba(var(--base-02), 1));
	}

	// Less then breakpoint.lg
	@media (max-width: 768px) {
		width: 100px;
		margin: 0;

		label {
			font-size: 1.5rem;
		}
	}
`;

const FROM_STATE = { from: '/' };

const dataLayerProductClickEvent = (product: string) => {
	insertTrackEvent({
		category: 'product buttons',
		action: 'clicked',
		label: product
	});
};

enum TextKeys {
	'LANDING_SPLASHTECHNEW' = 'LANDING_SPLASHTECHNEW',
	'MOBILE_MAIN_MENU_CASINO' = 'MOBILE_MAIN_MENU_CASINO',
	'MOBILE_MAIN_MENU_SPORTS' = 'MOBILE_MAIN_MENU_SPORTS',
	'MOBILE_MAIN_MENU_LIVECASINO' = 'MOBILE_MAIN_MENU_LIVECASINO',
	'MOBILE_MAIN_MENU_LIVEBETTING' = 'MOBILE_MAIN_MENU_LIVEBETTING',
	'MOBILE_MAIN_MENU_SHOP' = 'MOBILE_MAIN_MENU_SHOP',
	'MOBILE_MAIN_MENU_CAMPAIGN' = 'MOBILE_MAIN_MENU_CAMPAIGN',
	'MOBILE_MAIN_MENU_TVBET' = 'MOBILE_MAIN_MENU_TVBET',
	'MOBILE_GLOBAL_MENU_PROMOTION_PAGE' = 'MOBILE_GLOBAL_MENU_PROMOTION_PAGE',
	'MOBILE_LANDINGPAGE_JACKPOTS_LINK' = 'MOBILE_LANDINGPAGE_JACKPOTS_LINK',
	'MOBILE_LANDINGPAGE_JACKPOTS_LINK_TEXT' = 'MOBILE_LANDINGPAGE_JACKPOTS_LINK_TEXT',
	'MOBILE_MAIN_MENU_WESPIN' = 'MOBILE_MAIN_MENU_WESPIN',
	'NEW' = 'NEW',
	'SPLASH_TECH_PRODUCT_URL' = 'SPLASH_TECH_PRODUCT_URL',
	'PRODUCT_NAVBAR_VIRTUALS' = 'PRODUCT_NAVBAR_VIRTUALS',
	'PRODUCT_NAVBAR_FREE_TO_PLAY' = 'PRODUCT_NAVBAR_FREE_TO_PLAY'
}

type Props = {
	products: Array<string>;
	className?: string;
	productClassName?: string;
	globalMenuPromotionLink?: string;
};

const productIconWidth = '100px';

const ProductNav = memo<Props>(({ products, globalMenuPromotionLink, className, productClassName }) => {
	const { translations, isLoadingTranslations } = useTypedTranslations(TextKeys);

	if (isLoadingTranslations) return null;

	const {
		LANDING_SPLASHTECHNEW,
		MOBILE_MAIN_MENU_CASINO,
		MOBILE_MAIN_MENU_SPORTS,
		MOBILE_MAIN_MENU_LIVECASINO,
		MOBILE_MAIN_MENU_LIVEBETTING,
		MOBILE_MAIN_MENU_SHOP,
		MOBILE_MAIN_MENU_CAMPAIGN,
		PRODUCT_NAVBAR_VIRTUALS,
		MOBILE_MAIN_MENU_TVBET,
		MOBILE_GLOBAL_MENU_PROMOTION_PAGE,
		MOBILE_LANDINGPAGE_JACKPOTS_LINK,
		MOBILE_LANDINGPAGE_JACKPOTS_LINK_TEXT,
		MOBILE_MAIN_MENU_WESPIN,
		SPLASH_TECH_PRODUCT_URL,
		PRODUCT_NAVBAR_FREE_TO_PLAY
	} = translations;

	return (
		<IconWrapper numProducts={products.length} className={className}>
			{products
				.map(product => {
					switch (product) {
						case 'sportsbook':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_SPORTSBOOK
									}}
									state={FROM_STATE}
									label={MOBILE_MAIN_MENU_SPORTS}
									type="sportsbook"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'live-betting':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_LIVEBETTING
									}}
									state={FROM_STATE}
									label={MOBILE_MAIN_MENU_LIVEBETTING}
									type="sportsbook"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'casino':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_CASINO_EXPLORE
									}}
									state={FROM_STATE}
									label={MOBILE_MAIN_MENU_CASINO}
									type="casino-seven"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'we-spin':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_WE_SPIN
									}}
									state={FROM_STATE}
									label={MOBILE_MAIN_MENU_WESPIN}
									type="we-spin"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'livecasino':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_LIVE_CASINO_EXPLORE
									}}
									state={FROM_STATE}
									label={MOBILE_MAIN_MENU_LIVECASINO}
									type="dealer"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'jackpots':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									href={MOBILE_LANDINGPAGE_JACKPOTS_LINK}
									label={MOBILE_LANDINGPAGE_JACKPOTS_LINK_TEXT}
									type="jackpots"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'free-to-play':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_FREE_TO_PLAY,
										state: FROM_STATE
									}}
									label={PRODUCT_NAVBAR_FREE_TO_PLAY}
									type="free-to-play"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'campaign':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_SHOP
									}}
									state={FROM_STATE}
									label={MOBILE_MAIN_MENU_CAMPAIGN}
									type="campaign"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'shop':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_SHOP
									}}
									state={FROM_STATE}
									label={MOBILE_MAIN_MENU_SHOP}
									type="shop"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'virtuals':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_INSPIRED
									}}
									state={FROM_STATE}
									label={PRODUCT_NAVBAR_VIRTUALS}
									type="virtuals"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'splash-tech':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									href={SPLASH_TECH_PRODUCT_URL}
									label={LANDING_SPLASHTECHNEW}
									type="medal-star-line"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'tvbet':
							return (
								<ProductIconWithLabel
									width={productIconWidth}
									className={productClassName}
									key={product}
									to={{
										pathname: Paths.ROUTE_TVBET
									}}
									state={FROM_STATE}
									label={MOBILE_MAIN_MENU_TVBET}
									type="tvbet"
									dataAt={`product-${product}`}
									onClick={() => {
										dataLayerProductClickEvent(product);
									}}
								/>
							);
						case 'promotions':
							if (globalMenuPromotionLink) {
								return (
									<ProductIconWithLabel
										width={productIconWidth}
										className={productClassName}
										key={product}
										label={MOBILE_GLOBAL_MENU_PROMOTION_PAGE}
										type="campaign"
										target="_self"
										to={{
											pathname: Paths.ROUTE_PROMOTIONS
										}}
										state={FROM_STATE}
										dataAt={`product-${product}`}
										onClick={() => {
											dataLayerProductClickEvent(product);
										}}
									/>
								);
							}
							break;
						default:
							return null;
					}
					return null;
				})
				.filter(Boolean)}
		</IconWrapper>
	);
});

ProductNav.displayName = 'ProductNav';

export default ProductNav;
